'use client'
import * as React from 'react'
import { GraphiQL } from 'graphiql'
import { createGraphiQLFetcher } from '@graphiql/toolkit'
import 'graphiql/graphiql.css'
import './style.scss'
import './theme.css'
import { Label, LabeledInput } from '~/sotano-kit/primitives/input'
import { LabeledCheckbox } from '~/sotano-kit/primitives/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/sotano-kit/primitives/select'
import { clsx } from 'clsx'
import { useHasRendered } from '~/hooks/use-has-rendered'
import { useStorage } from '~/hooks/use-storage'
import { Branch, Commit, isCommit } from '@basehub/data/replicache/index'
import { Environment, getURL } from '@basehub/shared-constants'

type APIExplorerProps =
  | {
      variant: 'authenticated'
      readToken: string
      adminToken: string
      selectedBranchOrCommit: null | undefined | Branch | Commit
      repoId: string | undefined
    }
  | { variant: 'unauthenticated'; repoId: string | null | undefined }

export const APIExplorer = (props: APIExplorerProps) => {
  const hasRendered = useHasRendered()

  // Store API Explorer configuration in sessionStorage
  const [config, setConfig] = useStorage(
    'sessionStorage',
    (z) =>
      z.object({
        tokenInput: z.string(),
        refInput: z.string(),
        scope: z.enum(['read', 'write']),
        draftMode: z.boolean(),
      }),
    `api-explorer-config-${props.repoId ?? 'unauthenticated'}`,
    {
      enabled: props !== undefined,
      initialValue: {
        tokenInput: '',
        refInput: '',
        scope: 'read' as const,
        draftMode: false,
      },
    }
  )

  // Use default ref from selected branch/commit if refInput is empty
  const effectiveRef =
    config.refInput ||
    (props.variant === 'authenticated' && props.selectedBranchOrCommit
      ? isCommit(props.selectedBranchOrCommit)
        ? props.selectedBranchOrCommit.id
        : props.selectedBranchOrCommit.name
      : '')

  const token =
    props.variant === 'authenticated'
      ? config.scope === 'read'
        ? props.readToken
        : props.adminToken
      : config.tokenInput

  const fetcher = React.useMemo(() => {
    const url = new URL(
      '/graphql',
      getURL('api', process.env.NEXT_PUBLIC_ENVIRONMENT as Environment)
    )
    if (effectiveRef) url.searchParams.set('ref', effectiveRef)
    if (config.draftMode) url.searchParams.set('draft', 'true')

    url.searchParams.set('api-version', 'latest')

    return createGraphiQLFetcher({
      url: url.toString(),
      headers: {
        Authorization: `Bearer ${token}`,
      },
      fetch: (url, options) => {
        return fetch(url, {
          ...options,
          cache: 'no-store',
        })
      },
    })
  }, [token, effectiveRef, config.draftMode])

  const key = React.useMemo(() => {
    return token
  }, [token])

  return (
    <div
      className="[--inner-header-height:48px]"
      style={{
        height: 'calc(var(--editor-height) - var(--inner-header-height))',
      }}
      data-native-undo-redo
    >
      <header className="flex gap-8 h-[var(--inner-header-height)] bg-dark-base border-b border-dark-border-solid p-4 items-center justify-between">
        <div className="flex items-center gap-8">
          <div>
            {props.variant === 'authenticated' ? (
              <>
                <Label label="Scope" horizontal>
                  <Select
                    value={config.scope}
                    onValueChange={(value) =>
                      setConfig((prev) => ({
                        tokenInput: prev?.tokenInput ?? '',
                        refInput: prev?.refInput ?? '',
                        scope: value as 'read' | 'write',
                        draftMode: prev?.draftMode ?? false,
                      }))
                    }
                  >
                    <SelectTrigger aria-label="language" size="small" iconRight>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="read">Read Token</SelectItem>
                      <SelectItem value="write">Admin Token</SelectItem>
                    </SelectContent>
                  </Select>
                </Label>
              </>
            ) : (
              <>
                <LabeledInput
                  label="Token"
                  name="token"
                  inputProps={{
                    type: 'password',
                    onChange: (e) => {
                      setConfig((prev) => ({
                        tokenInput: e.target.value,
                        refInput: prev?.refInput ?? '',
                        scope: prev?.scope ?? 'read',
                        draftMode: prev?.draftMode ?? false,
                      }))
                    },
                    value: config.tokenInput,
                    size: 'xs',
                    autoComplete: 'off',
                  }}
                  className="!p-0 !m-0 w-64"
                  size="xs"
                  horizontal
                />
              </>
            )}
          </div>
          <div>
            <LabeledInput
              label="Ref"
              name="ref"
              inputProps={{
                onChange: (e) => {
                  setConfig((prev) => ({
                    tokenInput: prev?.tokenInput ?? '',
                    refInput: e.target.value,
                    scope: prev?.scope ?? 'read',
                    draftMode: prev?.draftMode ?? false,
                  }))
                },
                value: config.refInput,
                size: 'xs',
                autoComplete: 'off',
              }}
              draggable={false}
              className="!p-0 !m-0 w-64"
              size="xs"
              horizontal
            />
          </div>
          <div>
            <LabeledCheckbox
              label="Draft Mode"
              name="draft"
              checkboxProps={{
                onCheckedChange: (checked) => {
                  setConfig((prev) => ({
                    tokenInput: prev?.tokenInput ?? '',
                    refInput: prev?.refInput ?? '',
                    scope: prev?.scope ?? 'read',
                    draftMode: !!checked,
                  }))
                },
                className: 'w-64',
                checked: config.draftMode,
              }}
              className="!p-0 !m-0"
              size="xs"
            />
          </div>
        </div>
        <div>
          <a
            className="font-medium text-lg text-dark-muted"
            href="https://github.com/graphql/graphiql"
            rel="noopener"
            target="_blank"
          >
            Graph<em>i</em>QL
          </a>
        </div>
      </header>
      <div
        className={clsx(
          'w-full cm-s-ayu-mirage h-full',
          props.variant === 'authenticated'
            ? 'h-[calc(100vh-var(--inner-header-height)-var(--header-height))]'
            : 'h-[calc(100vh-var(--inner-header-height))]'
        )}
      >
        {hasRendered && (
          <GraphiQL
            fetcher={fetcher}
            key={key}
            response={
              token
                ? undefined
                : JSON.stringify(
                    {
                      message:
                        'Please enter your BASEHUB_TOKEN to get started.',
                    },
                    null,
                    2
                  )
            }
          >
            <GraphiQL.Logo>
              <></>
            </GraphiQL.Logo>
          </GraphiQL>
        )}
      </div>
    </div>
  )
}
